import { ClerkProvider, SignIn, useAuth } from '@clerk/react';
import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { createApi, type Api } from '../api/client.ts';

type Session = {
  api: Api;
  /** Stable per-identity key: local drafts are never restored under another identity. */
  userKey: string;
  mode: 'clerk' | 'dev';
  signOut(): void;
};

const SessionContext = createContext<Session | null>(null);

export function useSession(): Session {
  const session = useContext(SessionContext);
  if (!session) throw new Error('useSession outside of AuthRoot');
  return session;
}
export const useApi = () => useSession().api;

const CLERK_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined;
const DEV_AUTH = import.meta.env.VITE_MYSTIQ_DEV_AUTH === 'true';
const DEV_SUBJECT = 'mystiq.devSubject';
const AUTH_MODE = 'mystiq.authMode';

function storage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}
function store(key: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    // Storage may be unavailable (private mode); sign-in then lasts for this tab only.
  }
}

export function AuthRoot({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<'clerk' | 'dev'>(() => (CLERK_KEY && !(DEV_AUTH && storage(AUTH_MODE) === 'dev') ? 'clerk' : 'dev'));
  const useDev = () => {
    store(AUTH_MODE, 'dev');
    setMode('dev');
  };
  const useClerk = () => {
    store(AUTH_MODE, null);
    setMode('clerk');
  };
  if (mode === 'clerk' && CLERK_KEY) {
    return (
      <ClerkProvider publishableKey={CLERK_KEY} afterSignOutUrl="/">
        <ClerkSession onUseDev={DEV_AUTH ? useDev : undefined}>{children}</ClerkSession>
      </ClerkProvider>
    );
  }
  if (DEV_AUTH) return <DevSession onUseClerk={CLERK_KEY ? useClerk : undefined}>{children}</DevSession>;
  return (
    <div className="signin">
      <div className="signin-sheet">
        <h1 className="page-title">Mystiq</h1>
        <p>Sign-in is not configured. Set VITE_CLERK_PUBLISHABLE_KEY (or VITE_MYSTIQ_DEV_AUTH=true for local development) and reload.</p>
      </div>
    </div>
  );
}

function ClerkSession({ children, onUseDev }: { children: ReactNode; onUseDev: (() => void) | undefined }) {
  const { isLoaded, isSignedIn, getToken, userId, signOut } = useAuth();
  const api = useMemo(() => createApi(() => getToken()), [getToken]);
  if (!isLoaded) return <div className="signin"><p className="meta">Loading…</p></div>;
  if (!isSignedIn) {
    // Come back to the page that was opened (an invitation, a deep link), not to the start page.
    const returnTo = `${window.location.pathname}${window.location.search}`;
    return (
      <div className="signin">
        <div className="signin-sheet">
          <h1 className="page-title">Mystiq</h1>
          <SignIn routing="hash" withSignUp forceRedirectUrl={returnTo} signUpForceRedirectUrl={returnTo} />
          {/* DEV_AUTH is a build-time constant: production bundles drop this button entirely. */}
          {DEV_AUTH && onUseDev && (
            <button className="link-button" onClick={onUseDev}>
              Use a development identity instead
            </button>
          )}
        </div>
      </div>
    );
  }
  return (
    <SessionContext.Provider value={{ api, userKey: `clerk:${userId}`, mode: 'clerk', signOut: () => void signOut() }}>
      {children}
    </SessionContext.Provider>
  );
}

function DevSession({ children, onUseClerk }: { children: ReactNode; onUseClerk: (() => void) | undefined }) {
  const [subject, setSubject] = useState<string | null>(() => storage(DEV_SUBJECT));
  const api = useMemo(() => createApi(async () => (subject ? `dev:${subject}` : null)), [subject]);
  if (!subject) {
    return (
      <DevSignIn
        api={api}
        onPick={(picked) => {
          store(DEV_SUBJECT, picked);
          setSubject(picked);
        }}
        onUseClerk={onUseClerk}
      />
    );
  }
  const signOut = () => {
    store(DEV_SUBJECT, null);
    setSubject(null);
  };
  return <SessionContext.Provider value={{ api, userKey: `dev:${subject}`, mode: 'dev', signOut }}>{children}</SessionContext.Provider>;
}

function DevSignIn({ api, onPick, onUseClerk }: { api: Api; onPick(subject: string): void; onUseClerk: (() => void) | undefined }) {
  const identities = useQuery({ queryKey: ['dev-identities'], queryFn: () => api.devIdentities() });
  return (
    <div className="signin">
      <div className="signin-sheet">
        <div className="stack">
          <h1 className="page-title">Sign in to Mystiq</h1>
          <p className="meta">Development identities. They only exist while MYSTIQ_DEV_AUTH is on, and never in production.</p>
        </div>
        {identities.isError && <p><span className="state">Could not load identities.</span> Start the API and run pnpm db:seed.</p>}
        <ul className="rows">
          {identities.data?.map((identity) => (
            <li key={identity.subject}>
              <button className="row row-link" style={{ width: '100%', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer' }} onClick={() => onPick(identity.subject)}>
                <span className="thumb" aria-hidden>{identity.displayName.slice(0, 1)}</span>
                <span className="row-body">
                  <span className="row-title">{identity.displayName}</span>
                  <span className="row-meta">{identity.description}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
        {onUseClerk && (
          <button className="link-button" onClick={onUseClerk}>
            Sign in with your account instead
          </button>
        )}
      </div>
    </div>
  );
}
